import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldHalf, RotateCcw, AlertTriangle, ArrowLeft } from "lucide-react";
import { Toaster, toast } from "sonner";
import { ScanInput } from "@/components/ScanInput";
import { ScoreGauge } from "@/components/ScoreGauge";
import { DashboardStats } from "@/components/DashboardStats";
import { IssuesList } from "@/components/IssuesList";
import { ScanLoader } from "@/components/ScanLoader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Header() {
  return (
    <header className="border-b border-border bg-surface/60 backdrop-blur sticky top-0 z-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldHalf className="w-6 h-6 text-neon glow-neon" />
          <span className="font-display font-black text-lg tracking-tight">SHIP<span className="text-neon">SAFE</span></span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground hidden sm:block">ship-security scanner</span>
      </div>
    </header>
  );
}

export default function App() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lastReq, setLastReq] = useState(null);

  const runScan = async (payload) => {
    setLoading(true);
    setLastReq(payload);
    try {
      const { data } = await axios.post(`${API}/scan`, payload, { timeout: 110000 });
      setResult(data);
      toast.success(`Scan complete — ${data.total_issues} issue${data.total_issues === 1 ? "" : "s"} found`);
    } catch (e) {
      let msg = e?.response?.data?.detail || "Scan failed. Please try again.";
      if (e?.code === "ECONNABORTED") {
        msg = "That repo is large and the scan timed out. Try 'Common files' depth, or turn off AI analysis for a faster pass.";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const rescan = () => { if (lastReq) runScan(lastReq); };
  const reset = () => { setResult(null); setLastReq(null); };

  return (
    <div className="App noise min-h-screen">
      <Toaster position="top-right" theme="dark" />
      <Header />

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-10 lg:py-16 relative z-10">
        <AnimatePresence mode="wait">
          {!result && !loading && (
            <motion.div key="intro" exit={{ opacity: 0 }}>
              <div className="mb-10 max-w-2xl">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-neon mb-4">// before you ship</p>
                <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                  Catch the security<br />mistakes<span className="text-neon">.</span>
                </h1>
                <p className="text-muted-foreground text-base mt-5 leading-relaxed">
                  Paste your code or drop a GitHub repo. We hunt exposed keys, leaked secrets,
                  tables without access rules, and unsafe queries — then hand you a copy-paste
                  prompt to fix each one.
                </p>
              </div>
              <ScanInput onScan={runScan} loading={loading} />
            </motion.div>
          )}

          {loading && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-2xl mx-auto pt-6">
              <h2 className="font-display font-bold text-2xl mb-5">Scanning<span className="text-neon animate-blink">_</span></h2>
              <ScanLoader />
            </motion.div>
          )}

          {result && !loading && (
            <motion.div key="results" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <button data-testid="new-scan-btn" onClick={reset} className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-neon transition-colors">
                  <ArrowLeft className="w-4 h-4" /> new scan
                </button>
                <button
                  data-testid="rescan-btn"
                  onClick={rescan}
                  className="inline-flex items-center gap-2 border border-neon text-neon font-mono uppercase tracking-widest text-xs px-5 py-2.5 hover:bg-neon hover:text-black transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> Re-scan after fixes
                </button>
              </div>

              <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground border border-border bg-surface px-4 py-2">
                <span className="text-neon">target:</span>
                <span className="text-foreground truncate">{result.source_label}</span>
                <span className="ml-auto shrink-0">{new Date(result.created_at).toLocaleString()}</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-1 h-full">
                  <ScoreGauge score={result.score} />
                </div>
                <div className="lg:col-span-2">
                  <DashboardStats result={result} />
                </div>
              </div>

              {result.score < 60 && result.total_issues > 0 && (
                <div className="flex items-start gap-3 border border-critical/40 bg-critical/5 p-4">
                  <AlertTriangle className="w-5 h-5 text-critical shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground/90">
                    <span className="font-semibold text-critical">Do not ship yet.</span> Fix the critical and high issues below, then hit <span className="font-mono text-neon">Re-scan</span> to confirm they're resolved.
                  </p>
                </div>
              )}

              <IssuesList issues={result.issues} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="border-t border-border mt-16 relative z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6 font-mono text-[11px] text-muted-foreground flex items-center justify-between flex-wrap gap-2">
          <span>SHIPSAFE // static + AI security analysis</span>
          <span>not a substitute for a full audit</span>
        </div>
      </footer>
    </div>
  );
}
